<script setup lang="ts">
import { computed, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';
import type { PlanRelayPreview, PlanRelayResult, Project } from '@/types/domain';

/**
 * 「重新铺设计划」—— 替代「删了重建」的那个动作。
 *
 * ## 为什么这个按钮值得存在
 *
 * 项目号是沿用的（Tony 0917），所以人想把计划推倒重来，唯一的路是新建一个项目。
 * 而新建一行 = 新 project_id = 33 张按 id 记事的表全部从零开始：
 * BOM、文件树、变更、团队、授权全留在旧行上。0916 的 DH26005 就是这么来的，
 * 人接着自己手工搬了一天多。
 *
 * 这个按钮让项目行不动，只重铺计划节点和交付物。
 *
 * ## 三条界面上的规矩
 *
 * 1. **必须先预览再确认。** 重铺会把一批旧活动标作废，看不到"会动哪些"就点确认
 *    等于蒙着眼签字。所以按钮点开是预览，确认按钮在预览出来之后才亮。
 * 2. **把"留下什么"说在"作废什么"前面。** 人怕的是丢东西，不是多东西；
 *    先看到"96 个有进展的原样保留"，才看得下去"88 个没人碰过的作废"。
 * 3. **列出要作废的名字。** 数字看不出对错，名字能——扫一眼就知道有没有不该动的。
 */
/*
  label / teleport（0930）：健康度明细里「项目计划尚未配置」那张卡就地铺计划，用的也是这个对话框——
  那里按钮叫「按标准模板铺计划」；对话框要挂到 body 上，不然被健康度抽屉（z-index 120）盖住。
*/
const props = defineProps<{ project: Project; disabled?: boolean; label?: string; teleport?: boolean }>();
const emit = defineEmits<{ (event: 'done', result: PlanRelayResult): void }>();


const open = ref(false);
const loading = ref(false);
const running = ref(false);
const preview = ref<PlanRelayPreview | null>(null);
const confirmed = ref(false);
/** 一个活动都没有的项目（老系统迁来没带计划）：这是「铺」不是「重铺」，没有保留和作废可说 */
const initialSeed = computed(() => Boolean(preview.value)
  && preview.value!.keeping === 0 && preview.value!.retiring === 0 && preview.value!.phases === 0);

async function openDialog(): Promise<void> {
  open.value = true;
  preview.value = null;
  confirmed.value = false;
  loading.value = true;
  try {
    preview.value = await eqsApi.previewPlanRelay(props.project.id);
  } catch (error) {
    notify('error', '预览失败', errorMessage(error));
    open.value = false;
  } finally {
    loading.value = false;
  }
}

function close(): void {
  open.value = false;
  preview.value = null;
  confirmed.value = false;
}

async function run(): Promise<void> {
  if (!confirmed.value) {
    notify('error', '请先勾选确认', '重铺会把一批旧活动作废，确认之后才会执行。');
    return;
  }
  const seeding = initialSeed.value;
  running.value = true;
  try {
    const result = await eqsApi.relayPlan(props.project.id);
    notify(
      'success',
      seeding ? '计划已铺好' : '计划已重新铺设',
      seeding
        ? `按标准模板铺了 ${result.addedNodes} 个活动、${result.addedDeliverables} 条交付物；`
          + '阶段日期按项目起止日期排的，到阶段工作里逐项微调。已有的里程碑、文件、BOM 都没动。'
        : `新增 ${result.addedNodes} 个活动、${result.addedDeliverables} 条交付物；`
          + `作废 ${result.retiredNodes} 个没人碰过的旧活动；`
          + `原样保留 ${result.keeping} 个有进展的。项目本身没有重建，BOM、文件、变更都在原处。`,
    );
    emit('done', result);
    close();
  } catch (error) {
    notify('error', '重铺失败', errorMessage(error));
  } finally {
    running.value = false;
  }
}
</script>

<template>
  <button
    class="button button-secondary"
    type="button"
    :disabled="disabled"
    title="在本项目上按标准模板铺计划；项目不会重建，BOM、文件、变更、团队都留在原处"
    @click="openDialog"
  >{{ label || '重新铺设计划' }}</button>

  <Teleport to="body" :disabled="!teleport">
  <div v-if="open" class="relay-mask" @click.self="close">
    <section class="relay-panel" role="dialog" :aria-label="initialSeed ? '按标准模板铺计划' : '重新铺设计划'">
      <header class="relay-head">
        <div>
          <span>PLAN RELAY</span>
          <h3>{{ initialSeed ? '按标准模板铺计划' : '重新铺设计划' }} · {{ project.projectNo }}</h3>
        </div>
        <button class="inline-button" type="button" @click="close">关闭</button>
      </header>

      <p v-if="loading" class="relay-loading">正在算这次会动哪些活动…</p>

      <template v-else-if="preview && initialSeed">
        <p class="relay-lede">
          这个项目还没有计划。按标准模板（{{ preview.templateCode }} · {{ preview.templateRevision }}）
          铺 <b>{{ preview.adding }}</b> 个活动和对应的交付物，阶段日期按项目起止日期排。
          <b>项目本身不动</b>——已有的里程碑、文件、BOM、团队都留在原处。
        </p>
        <ul class="relay-facts">
          <li class="add">
            <b>+{{ preview.adding }}</b>
            <span>个活动<i>新增</i></span>
          </li>
        </ul>
        <label class="relay-confirm">
          <input v-model="confirmed" type="checkbox">
          <span>确认按标准模板铺这 {{ preview.adding }} 个活动。铺完到阶段工作里逐项调日期、定负责人。</span>
        </label>
        <footer class="relay-foot">
          <button class="button button-secondary" type="button" @click="close">取消</button>
          <button
            class="button button-primary"
            type="button"
            :disabled="running || !confirmed || !preview.adding"
            @click="run"
          >{{ running ? '正在铺设…' : '确认铺设' }}</button>
        </footer>
      </template>

      <template v-else-if="preview">
        <p class="relay-lede">
          按标准模板（{{ preview.templateCode }} · {{ preview.templateRevision }}）在
          <b>本项目上</b>重铺计划。<b>项目不会重建</b>——BOM、文件、工程变更、团队、授权、
          里程碑全都留在原处，只有计划活动和交付物按模板重排。
        </p>

        <ul class="relay-facts">
          <li class="keep">
            <b>{{ preview.keeping }}</b>
            <span>个有进展的活动<i>原样保留</i></span>
          </li>
          <li class="phase">
            <b>{{ preview.phases }}</b>
            <span>个阶段行<i>不动</i></span>
          </li>
          <li class="add">
            <b>+{{ preview.adding }}</b>
            <span>个活动<i>新增</i></span>
          </li>
          <li class="retire">
            <b>{{ preview.retiring }}</b>
            <span>个没人碰过的旧活动<i>作废</i></span>
          </li>
        </ul>

        <div v-if="preview.keepReasons.length" class="relay-block">
          <h4>这些为什么留下</h4>
          <ul class="relay-reasons">
            <li v-for="item in preview.keepReasons" :key="item.reason">
              {{ item.reason }} · <b>{{ item.count }}</b>
            </li>
          </ul>
          <p class="relay-note">
            填过实际完成日期、报过进度、传过交付物、签过审批的活动，一个都不会动。
          </p>
        </div>

        <div v-if="preview.retiringSample.length" class="relay-block">
          <h4>会作废的活动（前 {{ preview.retiringSample.length }} 个，共 {{ preview.retiring }} 个）</h4>
          <ul class="relay-sample">
            <li v-for="(name, index) in preview.retiringSample" :key="`${index}-${name}`">{{ name }}</li>
          </ul>
          <p class="relay-note">
            作废是<b>打标记</b>不是删除，随时能找管理员调出来核对。
            连带作废 {{ preview.retiringDeliverables }} 条还没人交东西的交付物；
            有正文或提交过的一条都不动。
          </p>
        </div>

        <p v-if="!preview.retiring && !preview.adding" class="relay-noop">
          这个项目的计划已经和标准模板一致，重铺不会有任何变化。
        </p>

        <label v-else class="relay-confirm">
          <input v-model="confirmed" type="checkbox">
          <span>
            我看过上面的清单，确认可以把这 {{ preview.retiring }} 个活动作废并按模板重铺。
          </span>
        </label>

        <footer class="relay-foot">
          <button class="button button-secondary" type="button" @click="close">取消</button>
          <button
            class="button button-primary"
            type="button"
            :disabled="running || !confirmed || (!preview.retiring && !preview.adding)"
            @click="run"
          >{{ running ? '正在重铺…' : '确认重铺' }}</button>
        </footer>
      </template>
    </section>
  </div>
  </Teleport>
</template>

<style scoped>
/*
  这个遮罩是 position: fixed。选择器必须写成**顶层**的（不带任何祖先前缀）——
  0917 栽过一次：给弹窗加样式时用子串替换，规则被套进了 `.dlv-actions` 下面，
  position: fixed 退化成 static，弹窗渲染在页面 1132px 处，界面上完全看不见，
  而 23 个单测全绿（jsdom 不做布局）。改这一段之后要在真浏览器里量一次。
*/
.relay-mask {
  position: fixed;
  inset: 0;
  /* 130：在健康度明细（120）之上、提示条（200）之下——健康度里就地铺计划时要看得见（0930） */
  z-index: 130;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.42);
}

.relay-panel {
  width: min(680px, 100%);
  max-height: min(86vh, 760px);
  overflow-y: auto;
  padding: 20px 22px 18px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
}

.relay-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.relay-head span {
  font-size: 11px;
  letter-spacing: 0.08em;
  color: #94a3b8;
}

.relay-head h3 {
  margin: 2px 0 0;
  font-size: 17px;
  color: #0f172a;
}

.relay-loading { margin: 24px 0; color: #64748b; }

.relay-lede {
  margin: 0 0 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f1f5f9;
  font-size: 13px;
  line-height: 1.7;
  color: #334155;
}

/* 「留下什么」排在最前面：人怕的是丢东西，不是多东西 */
.relay-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(126px, 1fr));
  gap: 8px;
  margin: 0 0 14px;
  padding: 0;
  list-style: none;
}

.relay-facts li {
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}

.relay-facts b { display: block; font-size: 21px; line-height: 1.2; }
.relay-facts span { font-size: 12px; color: #64748b; }
.relay-facts i { display: block; font-style: normal; font-weight: 600; color: #334155; }
.relay-facts .keep b { color: #0f9d58; }
.relay-facts .keep { border-color: #bbf7d0; background: #f0fdf4; }
.relay-facts .add b { color: #2563eb; }
.relay-facts .phase b { color: #64748b; }
.relay-facts .retire b { color: #d97706; }
.relay-facts .retire { border-color: #fde68a; background: #fffbeb; }

.relay-block { margin: 0 0 14px; }
.relay-block h4 { margin: 0 0 6px; font-size: 13px; color: #0f172a; }

.relay-reasons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.relay-reasons li {
  padding: 3px 9px;
  border: 1px solid #bbf7d0;
  border-radius: 999px;
  background: #f0fdf4;
  font-size: 12px;
  color: #166534;
}

.relay-sample {
  max-height: 168px;
  overflow-y: auto;
  margin: 0;
  padding: 8px 10px 8px 26px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.8;
  color: #475569;
}

.relay-note { margin: 6px 0 0; font-size: 12px; line-height: 1.7; color: #64748b; }
.relay-noop { margin: 0 0 14px; font-size: 13px; color: #0f9d58; }

.relay-confirm {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 14px;
  padding: 10px 12px;
  border: 1px solid #fde68a;
  border-radius: 10px;
  background: #fffbeb;
  font-size: 13px;
  line-height: 1.6;
  color: #92400e;
}

.relay-confirm input { margin-top: 3px; }

/*
  按钮钉在面板底部。
  在真浏览器里量过（1440x900）：面板 680x760，内容比它高，
  footer 原来跟着内容滚到了折线以下——「确认重铺」这个主动作要先滚一下才看得见。
  主动作不该藏在滚动条后面，所以 sticky 到底边，并留一条上边框区分内容。
*/
.relay-foot {
  position: sticky;
  bottom: -18px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin: 0 -22px -18px;
  padding: 12px 22px;
  border-top: 1px solid #e2e8f0;
  background: #fff;
}
</style>
